import React, { useState, useCallback, useEffect } from 'react';
import {
  getVesselSchedule,
  getAllVesselSchedule,
  getSslVesselSchedule,
  getPortsList,
  getTerminalsList,
  SaveApiResMessage,
  getTableList,
} from '@/actions';
import List from './List';
import SearchContainer from '../Appointments/SearchContainer';
import IconButton from '@/ctComponents/IconButton';
import Options from '@/components/Global/Options';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import Tabs from '@/components/Tabs';
import PageContainerAB from '@/components/PageContainerAB/pageContainer';
import { CustomFilter } from '@/components/Global/CustomFilter';
import { fDate2 } from '@/actions/Helper';
import CustomBadge from '@/components/CustomBadge';

const tabsData = [
  { title: 'All' },
  { title: 'Terminal' },
  { title: 'Shipping Line' },
];

const formatDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildApiParams = (filters, currentPage) => {
  const params = new URLSearchParams();

  params.append('page', currentPage);

  const isValidValue = (value) => {
    return (
      value !== undefined &&
      value !== null &&
      value !== '' &&
      value !== 'All' &&
      value !== 0
    );
  };

  if (isValidValue(filters.port?.value) && filters.port?.value) {
    params.append('port', filters.port?.value);
  }

  if (filters.vessel) {
    params.append('vessel', filters.vessel);
  }

  if (isValidValue(filters.terminal?.value) && filters.terminal?.value) {
    params.append('terminal', filters.terminal?.value);
  }

  if (isValidValue(filters.ssl?.value) && filters.ssl?.value) {
    params.append('line', filters.ssl?.label);
  }

  if (filters.eta) {
    const formattedDate = formatDate(filters.eta);
    if (formattedDate) {
      params.append('eta', formattedDate);
    }
  }

  if (filters.etd) {
    const formattedDate = formatDate(filters.etd);
    if (formattedDate) {
      params.append('etd', formattedDate);
    }
  }

  if (filters.erd) {
    const formattedDate = formatDate(filters.erd);
    if (formattedDate) {
      params.append('erd', formattedDate);
    }
  }

  if (filters.cutoff) {
    const formattedDate = formatDate(filters.cutoff);
    if (formattedDate) {
      params.append('cutoff', formattedDate);
    }
  }

  return params.toString();
};

const VesselSchedule = () => {
  const [portsList, setPortsList] = useState([]);
  const [terminalsList, setTerminalsList] = useState([]);
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [filter, setFilter] = React.useState({
    open: false,
    appliedFilters: {},
  });
  const [options, setOptions] = React.useState({
    open: false,
    DATA: [],
  });

  const [toggled, setToggled] = React.useState(false);
  const mainToggleButtonRef = React.useRef(null);
  const [tableType, setTableType] = useState('vesselScheduleTable');
  const [tableTheadTitle, settableTheadTitle] = React.useState([]);

  const fetchApi = useCallback(
    (page) => {
      if (activeTab === 0) {
        return getAllVesselSchedule(
          buildApiParams(filter.appliedFilters, page)
        );
      } else if (activeTab === 1) {
        return getVesselSchedule(buildApiParams(filter.appliedFilters, page));
      } else {
        return getSslVesselSchedule(
          buildApiParams(filter.appliedFilters, page)
        );
      }
    },
    [activeTab, filter.appliedFilters]
  );

  const { items: DATATable, loading: loadingData } = useInfiniteScroll(
    fetchApi,
    {
      deps: [activeTab, filter.appliedFilters],
      searchText: filter.appliedFilters.vessel,
      responseDataKey: 'vessels',
    }
  );

  const getPortsListFunc = () => {
    getPortsList()
      .then((res) => {
        const portsRes = Object.values(res.data.ports);
        if (portsRes) {
          setPortsList(portsRes);
        }
      })
      .catch((err) => {
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'warning',
        });
      });
  };

  const getTerminalsListFunc = () => {
    getTerminalsList()
      .then((res) => {
        const terminalsRes = Object.values(res.data.terminals);
        if (terminalsRes) {
          setTerminalsList(terminalsRes);
        }
      })
      .catch((err) => {
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'warning',
        });
      });
  };

  useEffect(() => {
    getPortsListFunc();
    getTerminalsListFunc();
    getVesselScheduleTableHeadList();
  }, []);

  const getVesselScheduleTableHeadList = () => {
    setLoading(true);

    getTableList('vesselScheduleTable')
      .then((res) => {
        if (res.data?.settings?.data) {
          settableTheadTitle(res.data?.settings?.data);
        } else {
          settableTheadTitle([
            { label: 'Terminal', flex: 4, show: true, object: 'terminal' },
            { label: 'Vessel', flex: 3, show: true, object: 'name' },
            {
              label: 'Inbound Voyage',
              flex: 2,
              show: true,
              object: 'voyage_in',
            },
            {
              label: 'Outbound Voyage',
              flex: 2,
              show: true,
              object: 'voyage_out',
            },
            { label: 'SSL', flex: 3, show: true, object: 'ssl' },
            { label: 'ETA', flex: 3, show: true, object: 'eta' },
            { label: 'ATA', flex: 3, show: true, object: 'ata' },
            { label: 'ETD', flex: 3, show: true, object: 'etd' },
            { label: 'ATD', flex: 3, show: true, object: 'atd' },
            { label: 'Empty Pickup', flex: 3, show: true, object: 'epd' },
            {
              label: 'ERD Refer',
              flex: 3,
              show: false,
              object: 'erd_reefer',
            },
            { label: 'ERD Dry', flex: 3, show: true, object: 'erd_dry' },
            {
              label: 'Cutoff Refer',
              flex: 3,
              show: false,
              object: 'cutoff_reefer',
            },
            {
              label: 'Cutoff Hazard',
              flex: 3,
              show: false,
              object: 'cutoff_hazard',
            },
            {
              label: 'Cutoff Dry',
              flex: 3,
              show: true,
              object: 'cutoff_dry',
            },
            {
              label: 'Extended Late Gate',
              flex: 3,
              show: false,
              object: 'elgd',
            },
            {
              label: 'Last Update',
              flex: 3,
              show: true,
              object: 'updated_at',
            },
          ]);
        }
        setLoading(false);
      })
      .catch((err) => {
        // notify(err, 'warning');
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'warning',
        });
        setLoading(false);
      });
  };

  const getFinalData = useCallback(() => {
    if (!DATATable || DATATable.length === 0) return DATATable;

    switch (activeTab) {
      case 0:
        return DATATable;
      case 1:
        return DATATable.filter(
          (vessel) => vessel.terminal && vessel.terminal.id
        );
      case 2:
        return DATATable.filter(
          (vessel) => vessel.shipping_line && vessel.shipping_line.id
        );
      default:
        return DATATable;
    }
  }, [DATATable, activeTab]);

  const hasAppliedFilters = () => {
    return (
      filter.appliedFilters &&
      Object.entries(filter.appliedFilters).filter(
        ([key, value]) => value && key !== 'vessel'
      ).length > 0
    );
  };
  console.log(filter.appliedFilters);
  const handleExport = () => {};

  const renderContent = () => (
    <div>
      <div className="flex flex-col lg:flex-row gap-3 justify-between lg:items-center my-5">
        <div className="max-w-344">
          <Tabs
            className="justify-center"
            tabs={tabsData}
            selectedTab={activeTab}
            onTabChange={setActiveTab}
          />
        </div>

        <div className="flex flex-wrap flex-col sm:flex-row gap-1 lg:gap-3 py-2  w-full lg:w-auto justify-start">
          <div className="w-full sm:w-268">
            <SearchContainer
              className="box-border"
              width={'100%'}
              placeholder="Search by Vessel..."
              setContainerName={(value) => {
                setFilter({
                  ...filter,
                  appliedFilters: { ...filter.appliedFilters, vessel: value },
                });
              }}
              setPage={() => {}}
            />
          </div>

          <IconButton
            ref={mainToggleButtonRef}
            active={options.open}
            text="Column"
            icon="options"
            color="#131517"
            onClick={() => {
              setOptions(
                options.open ? { open: false } : { ...options, open: true }
              );
              setToggled(true);
              setFilter({ ...filter, open: false });
            }}
          />
          <IconButton
            active={filter.open || hasAppliedFilters()}
            text="Filter"
            icon="filter"
            onClick={() => {
              setFilter({ ...filter, open: filter.open ? false : true });
              setOptions({ ...options, open: false });
            }}
          />
          <IconButton
            text="Export XLSX"
            icon="exportXLSXIcon"
            color="#131517"
            onClick={handleExport}
          />
        </div>
      </div>
      <Options
        noTitleAndIcon={true}
        options={options}
        tableType={tableType}
        setTableType={setTableType}
        tableTheadTitle={tableTheadTitle || []}
        toggled={toggled}
        setOptions={setOptions}
        mainToggleButtonRef={mainToggleButtonRef}
      />
      <CustomFilter
        filtersList={['port', 'terminal', 'ssl', 'eta', 'etd', 'erd', 'cutoff']}
        noTitleAndIcon={true}
        filter={filter}
        setFilter={setFilter}
        DATA={DATATable || []}
        portOptions={portsList}
        terminalOptions={terminalsList}
        activeTab={activeTab}
      />

      {hasAppliedFilters() && (
        <div className="relative z-99 w-full bg-white rounded-lg items-center flex flex-wrap gap-2 mb-4 py-3 ">
          <span className="text-sm">Filtered by:</span>
          {Object.entries(filter.appliedFilters)
            .filter(
              ([key, value]) =>
                value &&
                key !== 'vessel' &&
                key !== 'eta' &&
                key !== 'etd' &&
                key !== 'erd' &&
                key !== 'cutoff'
            )
            .map(([key, value]) => {
              return (
                <CustomBadge
                  key={key}
                  text={value?.label}
                  onClick={() => {
                    const newFilters = { ...filter.appliedFilters };
                    delete newFilters[key];

                    setFilter({
                      ...filter,
                      appliedFilters: newFilters,
                    });
                  }}
                />
              );
            })}
          {Object.entries(filter.appliedFilters)
            .filter(
              ([key, value]) =>
                value &&
                (key === 'eta' ||
                  key === 'etd' ||
                  key === 'erd' ||
                  key === 'cutoff')
            )
            .map(([key, value]) => {
              return (
                <CustomBadge
                  key={key}
                  text={`${key.toUpperCase()}: ` + fDate2(value)}
                  onClick={() => {
                    const newFilters = { ...filter.appliedFilters };
                    delete newFilters[key];

                    setFilter({
                      ...filter,
                      appliedFilters: newFilters,
                    });
                  }}
                />
              );
            })}
        </div>
      )}

      <List
        vesselsList={getFinalData()}
        activeTab={activeTab}
        loading={loading || loadingData}
        tableTheadTitle={tableTheadTitle}
      />
    </div>
  );

  return (
    <PageContainerAB minWidth={'unset'} title="Vessel Schedule" mainPage>
      {renderContent()}
    </PageContainerAB>
  );
};

export default VesselSchedule;
